import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CloudOff, RefreshCw, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/states';
import { useConfirm } from '@/components/ui/confirm';
import { cn } from '@/lib/utils';
import { useSync } from '@/Hooks/useSync';
import { useAppDispatch } from '@/Redux/store';
import { discardAllFailed, discardFailed, retryFailed } from '@/Redux/Slices/syncSlice';
import type { DeadLetter } from '@/lib/outbox';

/**
 * §28 — the one thing an operator must always be able to see: whether their
 * work has actually reached the server yet.
 *
 * Two states, and the second one is the point. "Pending" is normal and patient.
 * **"Rejected" is not**: those mutations were refused on the merits and sit in
 * the outbox dead letter until a human retries or discards them. A queue count
 * alone would read "nothing pending" while a rejected order waits on disk —
 * true about the queue, and a lie about the operator's work. So the rejected
 * count wins the banner whenever it is non-zero, and it carries the server's
 * own message rather than a generic failure.
 *
 * It renders nothing at all when there is nothing to say; a banner that is
 * always there is a banner nobody reads.
 */
export function SyncBanner() {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { confirm } = useConfirm();
  const { online, pending, syncing, deadLetters } = useSync();
  const [open, setOpen] = useState(false);

  const failed = deadLetters.length;
  const quiet = online && pending === 0 && failed === 0;
  // The dialog outlives the bar: discarding the last dead letter would
  // otherwise rip the panel away mid-decision.
  if (quiet && !open) return null;

  const describe = (letter: DeadLetter) =>
    letter.entry.label ?? `${letter.entry.method.toUpperCase()} ${letter.entry.url}`;
  const when = (at: number) => new Date(at).toLocaleString(i18n.language);

  const discardOne = async (letter: DeadLetter) => {
    // Never `window.confirm`: it cannot be translated and a till in kiosk mode
    // suppresses it outright, which would turn Discard into a silent no-op.
    const ok = await confirm({
      title: t('common.sync.discardTitle'),
      body: t('common.sync.discardBody', { label: describe(letter) }),
      confirmLabel: t('common.sync.discard'),
      tone: 'danger',
    });
    if (ok) void dispatch(discardFailed(letter.entry.operationId));
  };

  const discardEverything = async () => {
    const ok = await confirm({
      title: t('common.sync.discardAllTitle'),
      body: t('common.sync.discardAllBody', { count: failed }),
      confirmLabel: t('common.sync.discardAll'),
      tone: 'danger',
    });
    if (ok) void dispatch(discardAllFailed());
  };

  const { tone, Icon, message } = failed
    ? {
        tone: 'border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/50 text-red-700 dark:text-red-400',
        Icon: TriangleAlert,
        message: t('common.sync.failed', { count: failed }),
      }
    : !online
      ? {
          tone: 'border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/50 text-amber-800 dark:text-amber-300',
          Icon: CloudOff,
          message: pending
            ? t('common.sync.offline', { count: pending })
            : t('common.sync.offlineIdle'),
        }
      : {
          tone: 'border-sky-200 dark:border-sky-900 bg-sky-50 dark:bg-sky-950/50 text-sky-700 dark:text-sky-400',
          Icon: RefreshCw,
          message: syncing
            ? t('common.sync.syncing', { count: pending })
            : t('common.sync.pending', { count: pending }),
        };

  return (
    <>
      {!quiet && (
        <div
          role="status"
          className={cn('flex items-center gap-3 border-b px-4 py-2 text-sm md:px-6', tone)}
        >
          <Icon className={cn('h-4 w-4 shrink-0', syncing && !failed && 'animate-spin')} />
          <span className="min-w-0 flex-1 truncate font-medium">{message}</span>
          {failed > 0 && (
            <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>
              {t('common.sync.review')}
            </Button>
          )}
        </div>
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title={t('common.sync.failedTitle')}
        description={t('common.sync.failedHint')}
        className="max-w-2xl"
        footer={
          <>
            {failed > 1 && (
              <Button variant="danger" onClick={() => void discardEverything()}>
                {t('common.sync.discardAll')}
              </Button>
            )}
            <Button variant="secondary" onClick={() => setOpen(false)}>
              {t('common.close')}
            </Button>
          </>
        }
      >
        {failed === 0 ? (
          <EmptyState title={t('common.sync.none')} />
        ) : (
          <div className="space-y-3">
            {deadLetters.map((letter) => (
              <div key={letter.entry.operationId} className="rounded-lg border p-4">
                <p className="font-medium">{describe(letter)}</p>
                <p className="mt-0.5 text-xs text-ink-muted">
                  {letter.status === null
                    ? t('common.sync.noStatus', { time: when(letter.failedAt) })
                    : t('common.sync.failedAt', {
                        status: letter.status,
                        time: when(letter.failedAt),
                      })}
                </p>
                {/* The server's own words. A generic "failed" here would leave the
                    operator guessing at a reason the server already gave. */}
                <p className="mt-2 text-sm text-red-700 dark:text-red-400">
                  {t('common.sync.serverSaid', { message: letter.message })}
                </p>
                <div className="mt-3 flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => void dispatch(retryFailed(letter.entry.operationId))}
                  >
                    {t('common.sync.retry')}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => void discardOne(letter)}>
                    {t('common.sync.discard')}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {pending > 0 && (
          <p className="mt-4 text-sm text-ink-muted">
            {t('common.sync.stillQueued', { count: pending })}
          </p>
        )}
      </Dialog>
    </>
  );
}
